import { publicEnv } from '@/shared/config/publicEnv'
import { Link, usePathname } from '@/navigation'
import { resolvePath } from '@/shared/utils/routesUtils'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import type { LucideProps } from 'lucide-react'
import {
    ArrowRight,
    BadgePlus,
    BarChartBig,
    Box,
    CircleCheck,
    Cog,
    FileCheck2,
    FileMinus,
    FileUp,
    PieChart,
    ReceiptText,
    ReplyAll,
    TextSelect,
    UserRoundCheck,
    UserRoundPlus,
    UsersRound
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { ForwardRefExoticComponent, RefAttributes } from 'react'
import SkeletonAvatar from '../common/Skeleton/SkeletonAvatar'
import type { Company } from '@/shared/models/companyModel'
import { resolveBase64Namespace } from '@/shared/utils/domUtils'
import Icon from '@/shared/v2/shared/components/Icon'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import { cva } from 'class-variance-authority'
import { getFeatureFlags, parseListEnvVar } from '@/shared/utils/featureFlagUtils'
import { useCompanyConfig } from '@/modules/company-configs/hooks/useCompanyConfig'

export type SidebarProps = {
    company?: Company
    isLoading?: boolean
    isGod: boolean
}

export interface PageAttributes {
    path: string
    label: string
    isVisible: boolean
    subPages?: string[]
    icon: ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>
}

export interface PageGroup {
    title?: string
    pages: PageAttributes[]
}

const resolveIsActivePage = (page: PageAttributes, pathname: string) => {
    const isExact = pathname === page.path
    const isSubPage = page.subPages?.some((subPage) => pathname.includes(subPage))
    return isExact || isSubPage
}

const linkClasses = cva('flex gap-2 items-center h-8 text-sm font-normal transition-all rounded-2 px-2', {
    variants: {
        active: {
            true: 'bg-backgroundPrimarySelect text-textSelect',
            false: 'text-textPrimary hover:bg-backgroundSecondaryHover hover:text-textPrimary'
        }
    }
})

const NavLink = ({ page, pathname }: { page: PageAttributes; pathname: string }) => {
    if (!page.isVisible) return null

    const isActive = resolveIsActivePage(page, pathname)

    return (
        <Link href={page.path} passHref>
            <div className={linkClasses({ active: isActive })} data-testid="__sidebar-link__">
                <Icon
                    strokeWidth={2}
                    color={isActive ? 'positive' : 'primary'}
                    icon={page.icon}
                    aria-label={page.label}
                />
                <div className="flex items-center justify-between w-full">
                    <span data-testid="__sidebar-link-label__">{page.label}</span>
                </div>
            </div>
        </Link>
    )
}

const PageGroupSection = ({
    group,
    groupIndex,
    pathname
}: {
    group: PageGroup
    groupIndex: number
    pathname: string
}) => (
    <div className="flex flex-col px-2 gap-4" data-testid={`__sidebar-page-group__-${groupIndex}`}>
        {group.title && (
            <Paragraph
                size={1}
                className="text-textSecondary uppercase px-2"
                data-testid="__sidebar-page-group-title__"
            >
                {group.title}
            </Paragraph>
        )}
        <div className="flex flex-col gap-0.5">
            {group.pages.map((page) => (
                <NavLink key={page.path} page={page} pathname={pathname} />
            ))}
        </div>
    </div>
)

const Sidebar = ({ company, isLoading, isGod }: SidebarProps) => {
    const t = useTranslations('root')
    const pathname = usePathname()
    const isSandbox = publicEnv.sandbox === 'true'

    const hasReceivables = company?.config.receivable.enabled ?? false
    const hasQuotes = company?.config.areQuotesAvailable ?? false
    const hasCreditMemos = company?.config.creditMemo.enabled ?? false
    const hasCsvImports = parseListEnvVar(publicEnv.csvImportsClientIds).includes(company?.id ?? '')
    const { hasUserLimits } = getFeatureFlags()

    const companyConfig = useCompanyConfig()

    const pageGroups: PageGroup[] = [
        {
            pages: [
                {
                    path: resolvePath('/'),
                    label: t('navigation.tabs.loans'),
                    isVisible: true,
                    subPages: ['loans'],
                    icon: BadgePlus
                },
                {
                    path: resolvePath('/repayments'),
                    label: t('navigation.tabs.repayments'),
                    isVisible: true,
                    subPages: ['repayments'],
                    icon: ReplyAll
                },
                {
                    path: resolvePath('/redirections'),
                    label: t('navigation.tabs.redirections'),
                    isVisible: true,
                    subPages: ['redirections'],
                    icon: ArrowRight
                },
                {
                    path: resolvePath('/credit-limits'),
                    label: t('navigation.tabs.creditLimits'),
                    isVisible: true,
                    subPages: ['credit-limits'],
                    icon: PieChart
                },
                {
                    path: resolvePath('/user-limits'),
                    label: t('navigation.tabs.userLimits'),
                    isVisible: hasUserLimits && (companyConfig.data?.body?.beneficiaryLimit?.enabled ?? false),
                    subPages: ['user-limits'],
                    icon: UserRoundPlus
                },
                {
                    path: resolvePath('/quotes'),
                    label: t('pages.quotes'),
                    isVisible: hasQuotes,
                    subPages: ['quotes'],
                    icon: CircleCheck
                }
            ]
        },
        {
            title: t('navigation.tabs.groups.entities'),
            pages: [
                {
                    path: resolvePath('/invoices'),
                    label: t('pages.invoices'),
                    isVisible: true,
                    subPages: ['invoices'],
                    icon: FileCheck2
                },
                {
                    path: resolvePath('/receivables'),
                    label: t('pages.receivables'),
                    isVisible: hasReceivables,
                    subPages: ['receivables'],
                    icon: ReceiptText
                },
                {
                    path: resolvePath('/credit-memos'),
                    label: t('navigation.tabs.creditMemos'),
                    isVisible: hasCreditMemos,
                    subPages: ['credit-memos'],
                    icon: FileMinus
                },
                {
                    path: resolvePath('/debtors'),
                    label: t('pages.debtors'),
                    isVisible: true,
                    subPages: ['debtors'],
                    icon: UsersRound
                },
                {
                    path: resolvePath('/users'),
                    label: t('pages.users'),
                    isVisible: true,
                    subPages: ['users'],
                    icon: UserRoundCheck
                }
            ]
        },
        {
            title: t('navigation.tabs.groups.discover'),
            pages: [
                {
                    path: resolvePath('/analytics'),
                    label: t('pages.analytics'),
                    isVisible: true,
                    subPages: ['analytics'],
                    icon: BarChartBig
                },
                ...(hasQuotes
                    ? []
                    : [
                          {
                              path: resolvePath('/quotes'),
                              label: t('pages.quotes'),
                              isVisible: true,
                              subPages: ['quotes'],
                              icon: TextSelect
                          }
                      ]),
                ...(hasReceivables
                    ? []
                    : [
                          {
                              path: resolvePath('/receivables'),
                              label: t('pages.receivables'),
                              isVisible: true,
                              subPages: ['receivables'],
                              icon: TextSelect
                          }
                      ]),
                ...(hasCreditMemos
                    ? []
                    : [
                          {
                              path: resolvePath('/credit-memos'),
                              label: t('navigation.tabs.creditMemos'),
                              isVisible: true,
                              subPages: ['credit-memos'],
                              icon: TextSelect
                          }
                      ])
            ]
        }
    ]

    const bottomGroup: PageGroup = {
        title: t('pages.developers'),
        pages: [
            {
                path: resolvePath('/imports'),
                label: t('pages.imports'),
                isVisible: hasCsvImports,
                subPages: ['imports'],
                icon: FileUp
            },
            {
                path: resolvePath('/settings'),
                label: t('pages.settings'),
                isVisible: true,
                subPages: ['settings', 'members', 'repayment-methods', 'preferences'],
                icon: Cog
            },
            {
                path: resolvePath('/webhooks'),
                label: t('pages.developers'),
                isVisible: true,
                subPages: ['webhooks', 'event-simulator', 'authentication'],
                icon: Box
            }
        ]
    }

    const companyName = `${company?.name} ${isGod ? ' - ADMIN' : ''}`

    return (
        <div id="sidebar" data-testid="__sidebar__" className="h-full flex flex-col bg-backgroundSecondary relative">
            <div className="flex-1 overflow-y-auto p-2 min-h-0">
                <div className="flex h-20 items-center p-2 mb-2">
                    <div data-testid="__sidebar-company__">
                        {isLoading ? (
                            <SkeletonAvatar />
                        ) : (
                            <div className="flex gap-2 items-center px-2">
                                <AvatarPrimitive.Root className="w-8 h-8">
                                    <AvatarPrimitive.Image
                                        className="rounded-3"
                                        src={
                                            company?.logo
                                                ? `${resolveBase64Namespace(company?.logo)};base64,${company?.logo}`
                                                : undefined
                                        }
                                    />
                                    <AvatarPrimitive.Fallback>
                                        {company?.name?.[0].toUpperCase() ?? ''}
                                    </AvatarPrimitive.Fallback>
                                </AvatarPrimitive.Root>
                                <div>
                                    <Paragraph
                                        size={3}
                                        variant="medium"
                                        data-testid="__sidebar-title__"
                                        className="leading-none pb-1"
                                    >
                                        {companyName}
                                    </Paragraph>
                                    <Paragraph
                                        size={2}
                                        color="brand"
                                        data-testid="__sidebar-environment__"
                                        className="leading-none text-textSelect"
                                    >
                                        {isSandbox ? 'Sandbox' : 'Production'}
                                    </Paragraph>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-4 mb-2">
                    {pageGroups.map((group, index) => (
                        // eslint-disable-next-line react/no-array-index-key -- a display-only list: its rows hold no state, and their values can repeat
                        <PageGroupSection key={index} group={group} groupIndex={index} pathname={pathname} />
                    ))}
                </div>
            </div>

            <div className="relative">
                <div className="absolute -top-8 left-0 right-3 h-9 bg-linear-to-b from-transparent to-backgroundSecondary pointer-events-none" />
                <div className="bg-backgroundSecondary">
                    <div className="flex flex-col gap-0.5 p-4">
                        {bottomGroup.pages.map((page) => (
                            <NavLink key={page.path} page={page} pathname={pathname} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Sidebar
